<template>
    <div
        :style="componentStyles"
        :class="['o-tiles-grid-with-scroll', frameSearchCssClass]"
    >
        <div class="tiles-grid-with-scroll-header">
            <OBadge :view="view"/>
            <OFiltersPanel
                v-if="isCategoryFilter"
                :category-id="categoryId"
                :view="view"
                @onCategoryChange="changeCategoryId"
            />
            <img
                v-if="isShowBadgeImage"
                class="badge-image"
                :src="badgeImageSrc" 
                alt="badge image"
            >
            <ATileQr 
                v-if="isShowTileQr"
                :view="view"
            />
        </div>

        <template v-if="searchResults.length">
            <OTilesPagination
                v-if="isPagination"
                :view="view"
                :items="searchResults"
                :items-per-block="itemsPerBlock"
            />
            <OTilesScrollable
                v-else
                :view="view"
                :items="chunkItems"
                :is-scroll-button="isScrollButton"
                :tile-type="tileType"
                :tile-height="tileHeight"
                @tileClick="handleTileClick"
            />
        </template>
        <ATilesSearchResults v-else />

        <OSortingPanel
            v-if="isAzFilter"
            v-model="sortBy"
            :view="view"
            :definedButtons="sortingButtonsValues"
        />
        <OKeyboardStandard
            v-if="isKeyboard"
            :selected-category-id="categoryId"
            :view="view"
            :searchResultsLength="searchResults.length"
            @keyboardChangeState="handleKeyboardChange"
        />
    </div>
</template>

<script>
import Fuse from "fuse.js";

import {
    mapState,
    mapGetters,
    mapActions
} from 'vuex';

import OBadge from '../../organisms/o-badge';
import ATileQr from '../../atoms/a-tile-qr/a-tile-qr';
import OFiltersPanel from '../../organisms/o-filters-panel';
import OSortingPanel from '../../organisms/o-sorting-panel';
import OTilesPagination from '../../organisms/o-tiles-pagination';
import OTilesScrollable from '../../organisms/o-tiles-scrollable';
import OKeyboardStandard from '../../organisms/o-keyboard-standard';
import ATilesSearchResults from '../../atoms/a-tiles-no-searchresults';

import {
    ALL,
    BRANDS,
    TENANTS,
    SERVICES,
    ALL_LETTERS,
    TENANTS_AND_SERVICES,
    BRANDS_TENANTS_CATEGORIES
} from "./constants";
import {
    ERASE_SEARCH_STRING,
    NO_DIRECT_MATCHES,
    SEARCH_DATA_CHANGE, SUCCESS_SEARCH,
    UNSUCCESS_SEARCH
} from "@/utils/constants/bus-events-constants";
import debounce from "lodash/debounce";
import { EVENTS } from "../../../utils/constants/bus-events-constants";

export default {
    name: "OTilesGridWithScroll",
    data() {
        return {
            isComponentMounted: false,
            sortBy: ALL,
            categoryId: this.getCategoryId(),
            oldSearchValue: '',
            searchValue: '',
            searchDelay: 350,
            searchTimeout: null,
            searchString: '',
            itemsPerBlock: 24,
            isHandleClearEmit: false,
            canSendStatistics: true,
            isFrameSearch: false,
            isComponentActivated: false
        }
    },
    props: {
        view: {
            type: String,
            default: ''
        }
    },
    components: {
        OBadge,
        ATileQr,
        OSortingPanel,
        OFiltersPanel,
        OTilesPagination,
        OTilesScrollable,
        OKeyboardStandard,
        ATilesSearchResults
    },
    computed: {
        ...mapState({
            activeLocale: state => state.activeLocale,
            mapLanguages: state => state.map.meta.languages
        }),
        ...mapGetters([
            't',
            'getBrandsAZ',
            'getTenantsAZ',
            'getBrandsRoot',
            'getPropsMapping',
            'getCategoriesAZ',
            'getMapConfigProp',
            'getServiceCategoriesAZ',
            'getTenantCategoriesRoot',
            'getServiceCategoriesRoot',
            'getListDescendantsAndSelf',
            'getServicesCategoriesRootLevel'
        ]),
        filteredTenantsAZ() {
            return this.filterItemsByListsCategory(this.getTenantsAZ);
        },
        filteredBrandsAZ() {
            return this.filterItemsByListsCategory(this.getBrandsAZ);
        },
        filteredCategoriesAZ() {
            return this.filterItemsByListsCategory(this.getCategoriesAZ);
        },
        filteredServiceCategoriesAZ() {
            return this.filterItemsByIdCategory(this.getServicesCategoriesRootLevel);
        },
        allItems(){
            return [
                ...this.filteredTenantsAZ,
                ...this.filteredCategoriesAZ,
                ...this.filteredBrandsAZ,
                ...this.filteredServiceCategoriesAZ,
            ];
        },
        items() {
            switch (this.componentParams.data){
                case BRANDS_TENANTS_CATEGORIES:
                    return this.allItems;
                case BRANDS:
                    return this.filteredBrandsAZ;
                case SERVICES:
                    return this.getServicesCategoriesRootLevel;
                case TENANTS_AND_SERVICES:
                    return  [...this.filteredTenantsAZ, ...this.filteredServiceCategoriesAZ];
                case TENANTS:
                default:
                    return this.filteredTenantsAZ;
            }
        },
        commonConfigStyles() {
            return this.getMapConfigProp('components.OTilesGridWithScroll.styles') || {};
        },
        viewConfigStyles() {
            return this.getMapConfigProp(`views.${this.view}.mainFrame.OTilesGridWithScroll.styles`) || {};
        },
        componentStyles() {
            return {
                ...this.commonConfigStyles,
                ...this.viewConfigStyles
            }
        },
        commonConfigParams() {
            return this.getMapConfigProp('components.OTilesGridWithScroll.params') || {};
        },
        viewConfigParams() {
            return this.getMapConfigProp(`views.${this.view}.mainFrame.OTilesGridWithScroll.params`) || {};
        },
        componentParams() {
            return {
                ...this.commonConfigParams,
                ...this.viewConfigParams
            }
        },
        isScrollButton() {
            return this.componentParams.scrollButton || false;
        },
        tileType() {
            return this.componentParams.tileType || 'MTile';
        },
        tileHeight() {
            return this.componentParams.tileHeight || 116;
        },
        tileInRow() {
            return this.componentParams.tileInRow || 6;
        },
        sortingButtonsValues() {
            if(!this.isComponentMounted){
                return ALL_LETTERS;
            }
            const props = this.getPropsMapping;
            return Array.from(
                this.items.reduce(
                    (acc,item) => acc.add(
                        (item.meta[props.name]||item.meta['name']||'').charAt(0)
                    ) && acc
                    ,new Set()
                )
            );
        },
        sortedItems() {
            const props = this.getPropsMapping;
            if (this.sortBy && this.sortBy !== ALL) {
                return this.items.filter(item =>
                    this.sortBy.includes(
                        (item.meta[props.name] || item.meta['name'] || '').charAt(0).toUpperCase()
                    )
                );
            }
            return this.items;
        },

        chunkItems() {
            let searchResults = this.filtredSearchResult;
            searchResults = searchResults.sort((a, b) => {
                const item = a?.item || a;

                const categories = item.lists?.categories || [];
                const aIsBrands = categories.length > 0 && item.type !== 'tenant';

                const bItem = b?.item || b;

                const bCategories = bItem.lists?.categories || [];
                const bIsBrands = bCategories.length > 0 && bItem.type !== 'tenant';

                return (aIsBrands === bIsBrands)? 0 : aIsBrands ? 1 : -1;
            });

            const chunk = this.tileInRow;
            const result = [];

            for (let i = 0; i < searchResults.length; i+=chunk) {
                result.push({ id: i+chunk, items: searchResults.slice(i, i+chunk)})
            }

            return result;
        },
        wideSearchResults(){
            return this.searchValue ? (new Fuse(this.sortedItems, this.wideSearchOptions)).search(this.searchValue) : [];
        },
        strictSearchResults(){
            return this.searchValue ? (new Fuse(this.sortedItems, this.strictSearchOptions)).search(this.searchValue) : [];
        },
        filtredSearchResult() {
            return this.searchResults.filter(obj => {
                const item = obj?.item || obj;
                if(item.hasOwnProperty('objectsCount')) {
                    return item.objectsCount > 0
                }
                return true
            })
        },
        searchResults() {
            if(!this.isComponentMounted) return new Array(30).fill({meta:{},files:{},lists:{}}, 0, 30);

            if(!this.searchValue) return this.sortedItems;

            if(this.strictSearchResults.length) return this.strictSearchResults;

            if(this.isWideSearchResultShow) return this.wideSearchResults;

            return [];
        },
        isPagination() {
            return this.componentParams?.pagination || false;
        },
        isCategoryFilter(){
            return this.componentParams?.category_filter !== false;
        },
        isKeyboard(){
            return this.componentParams?.keyboard === true;
        },
        isAzFilter(){
            return this.componentParams?.az_filter !== false;
        },
        // ------------ Search params -------------

        isWideSearchResultShow() {
            return this.getMapConfigProp('features.search.wide', false)
        },
        fuseSearchKeys() {
            const languages = this.mapLanguages;
            const options = [
                {
                    name: 'search_index.key_0',
                    weight: 4
                },
                {
                    name: 'search_index.key_1',
                    weight: 4
                },
                {
                    name: 'search_index.key_2',
                    weight: 4
                },
                {
                    name: 'search_index.key_3',
                    weight: 4
                },
                {
                    name: `meta.name`,
                    weight: 3
                }
            ];
            languages.forEach(lang => {
                options.push({
                    name: `meta.name_${lang}`,
                    weight: 3
                }, {
                    name: `meta.alt_names_${lang}`,
                    weight: 2
                }, {
                    name: `meta.content_${lang}`,
                    weight: 0.5
                }, {
                    name: `meta.short_description_${lang}`,
                    weight: 1
                }, {
                    name: `meta.description_${lang}`,
                    weight: 1
                })
            });
            return options;
        },
        strictSearchOptions(){
            return {
                keys: this.fuseSearchKeys,
                ignoreLocation: this.getMapConfigProp('features.search.strict.ignoreLocation', true),
                threshold: this.getMapConfigProp('features.search.strict.threshold', 0.2),
                location: this.getMapConfigProp('features.search.strict.location', 8),
                distance: this.getMapConfigProp('features.search.strict.distance', 0)
            }
        },
        wideSearchOptions(){
            return {
                keys: this.fuseSearchKeys,
                ignoreLocation: true,
                threshold: this.getMapConfigProp('features.search.wide.threshold', 0.6),
                location: this.getMapConfigProp('features.search.wide.location', 1),
            }
        },
        numberLettersRequiredForStrictSearch () {
            return this.getMapConfigProp('features.search.numberLettersRequiredForStrictSearch', 3)
        },

        // ------------ End search params ---------
        isSearchStringErase() {
            return this.oldSearchValue.length >= 2
                && (this.oldSearchValue.length - this.searchValue.length)/this.oldSearchValue.length >= this.getMapConfigProp('features.search.unsuccessfulSearchRate', 0.6);
        },
        hasStrictResult() {
            return this.searchValue && this.strictSearchResults.length;
        },
        frameSearchCssClass() {
            return this.isFrameSearch ? 'frame-search' : null;
        },
        isShowTileQr() {
            return this.componentParams.is_show_tile_qr
        },
        isShowBadgeImage() {
            return this.componentParams.is_show_badge_image
        },
        badgeImageSrc() {
            const images = this.componentParams.badge_image_src

            if(images) {
                return images[this.activeLocale]
            }
            return ''
        }
    },
    mounted() {
        this.setMounted(100);
        this.emitDataChange();
        this.sendCategoryClickEvent(this.$route.params.categoryId);
        this.isComponentActivated = true;
        this.canSendStatistics = false
        setTimeout(() => {
            this.canSendStatistics = true
        }, 100)
        this.initListeners();
    },
    activated() {
        this.isComponentActivated = true;
        this.clearComponentState();
        this.canSendStatistics && this.sendCategoryClickEvent(this.$route.params.categoryId);
    },
    deactivated() {
        this.isComponentActivated = false;
        this.searchValue = ''
    },
    beforeDestroy() {
        this.removeListeners();
    },
    watch: {
        searchValue(value) {
            if(value >= this.oldSearchValue) this.oldSearchValue = value;
            this.$nextTick(() => {
                this.$bus.$emit(SEARCH_DATA_CHANGE, {
                    searchValue: this.searchValue,
                    oldValue: this.oldSearchValue,
                    searchResultsCount: this.searchResults.length,
                    categoryId: this.categoryId
                })
            })
        },
        isSearchStringErase(value) {
            if(value) {
                if(this.isHandleClearEmit) {
                    this.$bus.$emit(UNSUCCESS_SEARCH)
                } else {
                    this.$bus.$emit(ERASE_SEARCH_STRING, {
                        value1: this.oldSearchValue,
                        value2: "not_found",
                        value3: "erase",
                        target_id: this.categoryId || this.searchResults.length,
                        target: this.categoryId ? 'category' : ''
                    } )
                }
                this.oldSearchValue = this.searchValue;
            }
        },
        hasStrictResult() {
            debounce(newValue => {
                !newValue && this.$bus.$emit(NO_DIRECT_MATCHES);
            },2000)
        }
    },
    methods: {
        ...mapActions([
            'sendCategoryClickEvent'
        ]),
        initListeners() {
            this.$bus.$on(EVENTS.KEYBOARD_STANDARD.INIT_KEYBOARD, this.initFrameSearch);
            this.$bus.$on(EVENTS.KEYBOARD_STANDARD.REMOVE_VALUE, this.handleRemoveValue);
            this.$bus.$on(EVENTS.KEYBOARD_STANDARD.CLOSE_KEYBOARD, this.destroyFrameSearch);
            this.$bus.$on(EVENTS.KEYBOARD_STANDARD.CHANGE_SEARCH_QUERY, this.setSearchValue);
        },
        removeListeners() {
            this.$bus.$off(EVENTS.KEYBOARD_STANDARD.INIT_KEYBOARD, this.initFrameSearch);
            this.$bus.$off(EVENTS.KEYBOARD_STANDARD.REMOVE_VALUE, this.handleRemoveValue);
            this.$bus.$off(EVENTS.KEYBOARD_STANDARD.CLOSE_KEYBOARD, this.destroyFrameSearch);
            this.$bus.$off(EVENTS.KEYBOARD_STANDARD.CHANGE_SEARCH_QUERY, this.setSearchValue);
        },
        initFrameSearch() {
            this.isFrameSearch = true;
        },
        destroyFrameSearch() {
            this.isFrameSearch = false;
            this.searchValue = '';
        },
        filterItemsByIdCategory(items) {
            const categoryId = this.categoryId;
            if(categoryId === this.getServiceCategoriesRoot.id
                || categoryId === this.getTenantCategoriesRoot.id){
                return items;
            }
            return items.filter(item => item.id === categoryId);
        },
        filterItemsByListsCategory(items) {
            const categoryId = this.categoryId;
            if(categoryId === this.getTenantCategoriesRoot.id){
                return items;
            }
            return items.filter(item =>
                item?.lists?.categories?.includes(categoryId)
            );
        },
        emitDataChange() {
            this.$bus.$emit(SEARCH_DATA_CHANGE, {})
        },
        handleRemoveValue() {
            if (this.oldSearchValue.length > 2) {
                this.isHandleClearEmit = true;
                setTimeout(() => {
                    this.isHandleClearEmit = false;
                });
            }
        },
        clearComponentState() {
            this.sortBy = ALL;
            this.categoryId = this.getCategoryId();
            this.searchValue = '';
            this.searchString = '';
        },
        setMounted(time) {
            setTimeout(() => {
                this.isComponentMounted=true;
            }, time);
        },
        changeCategoryId(id) {
            this.categoryId = id;
            this.sortBy = ALL;
            this.sendCategoryClickEvent(this.categoryId);
        },
        setSearchValue(value) {
            if(this.isComponentActivated) this.searchValue = value;
        },
        handleKeyboardChange(value) {
            if (value) this.itemsPerBlock = 30;
            else this.itemsPerBlock = 24;
        },
        getCategoryId() {
            return Number(this.$route.path.split('/').find(el => el.includes('categoryId')).split('-')[1]);
        },
        handleTileClick(data) {
            this.searchValue && this.$bus.$emit(SUCCESS_SEARCH, {
                value1: this.searchValue,
                value2: "found",
                value3: "request_guide",
                target: 'object',
                target_type: data.type,
                target_id: data.id,
            })
        }
    }
}
</script>

<style lang="scss" src="./theme.scss" />
